/**
 * Navigate - the app's launcher, opened from the logo (and Ctrl/Cmd+K).
 *
 * THE 2026-09-30 OVERHAUL. Operator: "navigate menu needs an overhaul", "no
 * limitations", "needs to be adaptable for desktop and ipad, and iphone",
 * "tailoring to novice all the way thru to pro-grade". Design notes, the
 * measured baseline and the decisions it makes: `.notepads/navigate-overhaul.md`.
 *
 * WHY IT IS BODY-MOUNTED, NOT IN THE SHELL (a DEC-096 exception, like Matrix
 * and Timelines). It used to mount through `shellPortal()`, so it was laid out
 * in the PROJECT's frame and shrunk with it. Measured: an iPad project on an
 * iPhone scales to 33%, which put tile titles at 6 px; iPad portrait was 69%.
 * A launcher is app chrome, not project content - it has to fit the screen in
 * your hand, whatever frame the project was drawn for. The layout adapts by
 * the SHEET's own width (container queries) and by height, so a phone on its
 * side scrolls instead of squashing.
 *
 * NOVICE TO PRO, WITHOUT A MODE (SUITE-FACTS s0: the floor stays low, the
 * ceiling goes in the depth):
 *   - floor: every destination visible, grouped by what you are doing, one
 *     plain line each; empty things say "none yet"; "Get set up" appears only
 *     while something genuinely is not set up.
 *   - ceiling: search reaches everything, including what has no card (each
 *     project, timeline and page by name, every Settings section, Traffic
 *     Monitor, Macros, Edit/Test); Ctrl/Cmd+K from anywhere; type-anywhere;
 *     arrows + Enter; live counts on every card for a pre-gig scan.
 *
 * THE 2026-10-05 LAYOUT (operator: "easy on the eyes. easy to navigate the
 * whole app, quickly, easily"). The timeline editor's look (DEC-232/239: one
 * UI typeface, flat neutral panels, colour only where it is data) and a split
 * view: every destination in a list on the left, always visible, so moving
 * from one place to the next is one tap; on the right, the status row, then
 * the things people come here to DO, the project's pages in miniature, and
 * recent projects. A phone stacks them into one column. Getting started is
 * the "?" in the header (and stays in search and in Get set up).
 *
 * CONTRACTS KEPT FOR THE BOT (opbuttons-bot helpers + smokes): the classes
 * `logo-menu-modal`, `logo-menu-body`, `logo-menu-perf` (+ `data-on`),
 * `logo-menu-perf-toggle`, `logo-menu-perf-meta`, `logo-menu-perf-preflight`,
 * `logo-menu-project`, `logo-menu-new-project`, `logo-menu-timelines`,
 * `logo-menu-matrix`, `logo-menu-connected-devices`, `logo-menu-settings`,
 * `logo-menu-build(-line)`, `logo-menu-opid`; the root
 * is the only `.sk-modal[data-size="full"]`; PANIC sits in `.sk-mhead` left of
 * `.sk-mclose` and does not close anything; every destination closes Navigate
 * before opening, so the next modal's `.sk-mtitle` is the first on the page.
 */

import { createEffect, createMemo, createSignal, For, Index, on, onCleanup, onMount, Show, untrack, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import { timelineTotalBars } from '@opbuttons/schema';
import { onBack } from '../navigate/backButton.ts';
import { closeNavigate, navigateOpen, navigateWantsSearch, clearNavigateWantsSearch, navigatePane, openNavigate, openNavigateAt, setNavigatePane, type NavPane } from '../navigate/store.ts';
import { navigateDocked, dockHidden, setDockHidden } from '../navigate/dock.ts';
import { showPane } from '../navigate/panes.ts';
import { setNavigatePortal } from '../layout/shellPortal.ts';
import { ConnectPage } from './ConnectPage.tsx';
import { ConnectedDevices } from './ConnectedDevices.tsx';
import { RigStateManager } from './RigStateManager.tsx';
import { LightsManager } from './LightsManager.tsx';
import { PageManager } from './PageManager.tsx';
import { PreflightPanel } from './PreflightPanel.tsx';
import { SettingsPanel } from './SettingsPanel.tsx';
import SkinPicker from './SkinPicker.tsx';
import { ProjectBrowser } from './ProjectBrowser.tsx';
import { ItemPalette } from './ItemPalette.tsx';
import { TimelinesPane, MatrixPane, JumpBackIn } from './NavigatePanes.tsx';
import { AudioPane, openAudioPicker } from './AudioPane.tsx';
import { tracks as audioTracks } from '../timeline/testTrackStore.ts';
import type { Activity } from '../navigate/recentActivity.ts';
import { buildMatrixCsv } from '../matrix/export.ts';
import { downloadBlob, timestampForFilename } from '../bundle/download.ts';
import { rankEntries, matchRange, type SearchEntry } from '../navigate/search.ts';
import {
  performanceMode, setPerformanceMode, performanceModeSource, performanceModeUnsent,
  performanceModeProblem, performanceModeWaitingForBridge, describeSource, OWN_APP_ID,
} from '../opbridge/performanceMode.ts';
import { appUpdateReady, applyAppUpdateNow } from '../appUpdate.ts';
import { lastResult, running as preflightRunning, runAndStore } from '../performance/preflightStore.ts';
import {
  activeProject, activeProjectId, projects, switchProject, activePage, switchPage, addPage,
  hueOf, HUE_VALUES,
} from '../layout/projectStore.ts';
import { modifiedAtOf } from '../layout/projectMerge.ts';
import { canvasSizeOf, DEVICES } from '../layout/devices.ts';
import { toggleEditMode, isEditMode, selectItem } from '../layout/store.ts';
import {
  openSettings, openTrafficMonitor, activeSkin, SKINS,
  type SettingsSection,
} from '../settings/store.ts';
import { openMatrixViewer, matrixLibrary } from '../matrix/store.ts';
import { rigStateBanks } from '../rigState/store.ts';
import { fixtures } from '../fixtures/store.ts';
import { openTimelineEditor, timelinesInActiveProject, timelines as allTimelines, createTimeline, createLightingTimeline } from '../timeline/store.ts';
import { followTransport, transportNow } from '../timeline/transport.ts';
import { openMacrosEditor } from '../macros/store.ts';
import { authIdentity, signOutOpId, refreshIdentity } from '../auth/opid.ts';
import { bridges, getConnection, connectionsVersion, aggregateStatus } from '../opbridge/store.ts';
import { consumerRevoked, consumerDenied } from '../opbridge/consumerWs.ts';
import { PanicButton } from './PanicButton.tsx';
import { panicInTopBar } from '../performance/panicPlacement.ts';
import { helpGo, helpBack, helpPrevView, type HelpView } from '../help/store.ts';
import { HelpPane, helpHead, headOf } from './HelpPane.tsx';
import { myReportsUnread, refreshUnreadSoon } from '../diag/helpDesk.ts';

export interface NavigateProps {
  /** Project browser, list view. */
  onOpenProjects: () => void;
  /** Project browser, straight to the new-project form. */
  onNewProject: () => void;
  /** The add-item palette. It is an edit-mode surface, so App enters edit mode first. */
  onAddControl: () => void;
  /** The welcome view ("Getting started"). */
  onGettingStarted: () => void;
}

/* ─────────────── icons: 20x20, stroke, currentColor ───────────────
   Hand-drawn for Navigate (DEC-141), never borrowed from the DEC-128
   instrument sprite: a synth glyph on "open the Matrix" would be a wrong
   meaning wearing a familiar face. Lights has its OWN glyph now - it used to
   share Rig States', which made two cards read as one thing. */
function Svg(props: { children: JSX.Element; vb?: string }) {
  return (
    <svg viewBox={props.vb ?? '0 0 20 20'} width="20" height="20" aria-hidden="true"
      fill="none" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round">
      {props.children}
    </svg>
  );
}
const FOLDER = 'M2.5 5.5c0-.55.45-1 1-1h4l1.6 2h8.4c.55 0 1 .45 1 1v7.5c0 .55-.45 1-1 1h-14c-.55 0-1-.45-1-1z';
const ICONS = {
  project: () => <Svg><path d={FOLDER} /></Svg>,
  newProject: () => <Svg><path d={FOLDER} /><path d="M10 9v5M7.5 11.5h5" /></Svg>,
  add: () => <Svg><rect x="3" y="3" width="14" height="14" rx="3.2" /><path d="M10 6.8v6.4M6.8 10h6.4" /></Svg>,
  pages: () => <Svg><rect x="6" y="2.8" width="10.5" height="12.4" rx="1.6" /><path d="M3.5 6.2v9.6c0 .8.6 1.4 1.4 1.4h8.2" /></Svg>,
  page: () => <Svg><rect x="4.5" y="3" width="11" height="14" rx="1.6" /><path d="M7.5 7h5M7.5 10h5M7.5 13h3" /></Svg>,
  timelines: () => (
    <Svg>
      <path d="M3 6h8M3 10h13M3 14h6" />
      <circle cx="12.5" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="8" cy="14" r="1.5" fill="currentColor" stroke="none" />
    </Svg>
  ),
  /** The audio library (2026-10-13): a waveform. */
  audio: () => <Svg><path d="M3 10h1.5M6 7v6M8.5 4.5v11M11 7.5v5M13.5 5.5v9M16 8.5v3" /></Svg>,
  rigState: () => (
    <Svg>
      <path d="M10 12L5 6M10 12L10 5M10 12L15 6" />
      <circle cx="10" cy="12" r="1.9" />
      <circle cx="5" cy="6" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="10" cy="5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.4" fill="currentColor" stroke="none" />
    </Svg>
  ),
  computer: () => <Svg><rect x="2.5" y="3.5" width="15" height="10" rx="1.6" /><path d="M7 16.8h6M10 13.5v3.3" /></Svg>,
  pedal: () => <Svg><rect x="2.5" y="11" width="7" height="6.5" rx="1.4" /><path d="M9.5 13.5c2.5 0 3-1.5 4.5-3" /><circle cx="15.5" cy="9" r="2" /></Svg>,
  lights: () => <Svg><path d="M7 13.2c-1.6-1-2.6-2.7-2.6-4.6a5.6 5.6 0 0 1 11.2 0c0 1.9-1 3.6-2.6 4.6v1.6H7z" /><path d="M8 17.5h4" /></Svg>,
  matrix: () => (
    <Svg>
      <g fill="currentColor" stroke="none">
        <circle cx="5" cy="5" r="1.4" /><circle cx="10" cy="5" r="1.4" /><circle cx="15" cy="5" r="1.4" />
        <circle cx="5" cy="10" r="1.4" /><circle cx="10" cy="10" r="1.4" /><circle cx="15" cy="10" r="1.4" />
        <circle cx="5" cy="15" r="1.4" /><circle cx="10" cy="15" r="1.4" /><circle cx="15" cy="15" r="1.4" />
      </g>
    </Svg>
  ),
  settings: () => (
    <Svg vb="0 0 16 16">
      <circle cx="8" cy="8" r="4.6" stroke-width="1.2" />
      <circle cx="8" cy="8" r="1.75" stroke-width="1.1" />
      <path stroke-width="1.4" d="M8 1.6v1.8M8 12.6v1.8M1.6 8h1.8M12.6 8h1.8M3.5 3.5l1.25 1.25M11.25 11.25l1.25 1.25M3.5 12.5l1.25-1.25M11.25 4.75l1.25-1.25" />
    </Svg>
  ),
  look: () => <Svg><circle cx="10" cy="10" r="7" /><path d="M10 3a7 7 0 0 0 0 14z" fill="currentColor" stroke="none" /></Svg>,
  backup: () => <Svg><path d="M10 3v9M6.5 8.5L10 12l3.5-3.5" /><path d="M3.5 13v2.5c0 .6.4 1 1 1h11c.6 0 1-.4 1-1V13" /></Svg>,
  help: () => (
    <Svg>
      <circle cx="10" cy="10" r="7.3" />
      <path d="M8 8a2 2 0 1 1 2.8 1.8c-.5.2-.8.7-.8 1.2v.4" />
      <circle cx="10" cy="14.2" r=".9" fill="currentColor" stroke="none" />
    </Svg>
  ),
  perf: () => <Svg><circle cx="10" cy="10" r="7.4" /><path d="M8.3 6.9l5.2 3.1-5.2 3.1z" fill="currentColor" stroke="none" /></Svg>,
  play: () => <Svg><path d="M7 5.2l8 4.8-8 4.8z" /></Svg>,
  home: () => <Svg><path d="M3.5 9.2L10 3.8l6.5 5.4" /><path d="M5.5 8v8.2h9V8" /><path d="M8.6 16.2v-4.4h2.8v4.4" /></Svg>,
  overview: () => <Svg><rect x="3" y="3" width="6" height="6" rx="1.2" /><rect x="11" y="3" width="6" height="6" rx="1.2" /><rect x="3" y="11" width="6" height="6" rx="1.2" /><rect x="11" y="11" width="6" height="6" rx="1.2" /></Svg>,
  projects: () => <Svg><path d="M2.5 6.5c0-.55.45-1 1-1h3.5l1.5 1.8h8c.55 0 1 .45 1 1v7c0 .55-.45 1-1 1h-13c-.55 0-1-.45-1-1z" /><path d="M5 3.5h4l1 1.2" /></Svg>,
  stage: () => <Svg><rect x="2.8" y="4" width="14.4" height="10.5" rx="1.6" /><path d="M8.6 7.4l3.6 2-3.6 2z" fill="currentColor" stroke="none" /><path d="M7 17h6" /></Svg>,
  preflight: () => <Svg><rect x="4" y="3" width="12" height="15" rx="1.6" /><path d="M7.5 10.5l2 2 3.5-4" /><path d="M8 3v1.6h4V3" /></Svg>,
  search: () => <Svg><circle cx="8.5" cy="8.5" r="5.5" /><path d="M12.6 12.6L17 17" /></Svg>,
  macros: () => <Svg><path d="M15.5 8.5A6 6 0 1 0 16 12" /><path d="M16 4.5v4h-4" /></Svg>,
  traffic: () => <Svg><path d="M2.5 10h3l2-5 3 10 2-5h5" /></Svg>,
  edit: () => <Svg vb="0 0 16 16"><path d="M2.67 13.33h2.67L12.33 6.33a1.885 1.885 0 1 0 -2.67 -2.67l-7 7v2.67Z" /><path d="M9 4.33l2.67 2.67" /></Svg>,
} satisfies Record<string, () => JSX.Element>;
type IconName = keyof typeof ICONS;

/* Per-device conveniences (browser storage, wrapped: private mode must not break the menu). */
const SETUP_HIDDEN_KEY = 'opbuttons.navigate.setupHidden.v1';
function readFlag(key: string): boolean {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function writeFlag(key: string, on: boolean): void {
  try { if (on) localStorage.setItem(key, '1'); else localStorage.removeItem(key); } catch { /* convenience only */ }
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "2 minutes ago", "yesterday", "3 weeks ago": the pane heading's "Edited ..."
 *  (app-study.html). Words, not the recent rows' compact "2m". */
function longAge(ms: number, now: number = Date.now()): string {
  const m = Math.round(Math.max(0, now - ms) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m === 1 ? 'a minute ago' : `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? 'an hour ago' : `${h} hours ago`;
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 14) return `${d} days ago`;
  const w = Math.round(d / 7);
  if (w < 9) return `${w} weeks ago`;
  const mo = Math.round(d / 30);
  return mo <= 1 ? 'last month' : `${mo} months ago`;
}

/** "iPad 10.2-inch, landscape" - the study's wording for the frame. */
function frameWords(device: string, orientation: string): string {
  return `${DEVICES.find((d) => d.id === device)?.label ?? device}, ${orientation}`;
}

/** HH:MM (24h) of a wall-clock ms - the pre-flight card's "ran at" stamp. */
function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** The performance line when there is nothing to add: the switch's tooltip,
 *  not shown under it (app-study.html's header has the label only). */
const PERF_DEFAULT_LINE = 'Pauses background sync and keeps the screen awake. Switches every OP app.';

function isTextField(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement).isContentEditable;
}

type Tone = 'good' | 'warn' | 'bad' | undefined;
interface Badge { text: string; tone?: 'good' | 'warn' | 'empty'; dot?: 'good' | 'warn' | 'bad' }
interface Card {
  id: string;
  /** Kept class for the bot / probes (`logo-menu-matrix`, ...). */
  cls: string;
  icon: IconName;
  title: string;
  detail: string;
  badge?: Badge | null;
  keywords: string;
  open: () => void;
}
interface Group { id: 'build' | 'rig' | 'app'; title: string; cards: Card[] }

export function Navigate(props: NavigateProps) {
  // A touch-only screen gets no autofocus: focusing the search would raise the
  // on-screen keyboard over the menu on every open. An iPad with a trackpad
  // reports hover, so it gets desktop behaviour - it has a hardware keyboard.
  const touchOnly = typeof matchMedia === 'function' && matchMedia('(hover: none)').matches;

  let searchRef: HTMLInputElement | undefined;
  let returnFocus: Element | null = null;
  const [query, setQuery] = createSignal('');
  const [active, setActive] = createSignal(0);
  const [opidOpen, setOpidOpen] = createSignal(false);
  const [setupHidden, setSetupHidden] = createSignal(readFlag(SETUP_HIDDEN_KEY));
  const searching = () => query().trim().length > 0;

  /** Close first, then open the destination: the next modal must be the only
   *  one on the page (a smoke reads the first `.sk-mtitle`), and Navigate
   *  closing is what hands focus back. */
  function go(open: () => void): void {
    closeNavigate();
    open();
  }

  /* ─────────────── performance mode + pre-flight (DEC-216) ─────────────── */
  // Status line under the toggle. Says who switched it when OPBridge said, and
  // says plainly when a press here is waiting on an unreachable OPBridge
  // rather than pretending it landed. (Strings are asserted by smokes.)
  const perfStatusLine = createMemo((): string => {
    const problem = performanceModeProblem();
    if (problem) return problem;
    const on = performanceMode();
    if (performanceModeWaitingForBridge()) {
      return `${on ? 'ON' : 'OFF'} on this device - OPBridge is offline, the rest of the suite switches when it is back`;
    }
    if (performanceModeUnsent()) return on ? 'ON - switching the suite...' : 'OFF - switching the suite...';
    if (!on) return PERF_DEFAULT_LINE;
    const src = performanceModeSource();
    if (src && src.app === OWN_APP_ID && !src.machine) return 'ON - switched here';
    const who = describeSource(src);
    return who ? `ON - from ${who}` : 'ON';
  });

  // DEC-216 ADAPTED: pre-flight is its own card now, always visible, not a
  // line that only appeared while performance mode was ON. A novice learns it
  // exists; a pro sees the last result without switching anything.
  const preflight = createMemo((): { status: string; meta: string } => {
    if (preflightRunning()) return { status: 'running', meta: 'Checking...' };
    const r = lastResult();
    if (!r) return { status: 'none', meta: 'Check the rig before a show' };
    const verdict = r.worst === 'pass' ? 'All clear'
      : r.worst === 'warn' ? 'Warnings'
      : r.worst === 'fail' ? 'Failures'
      : 'Done';
    return { status: r.worst, meta: `${verdict} at ${hhmm(r.ranAtMs)}` };
  });
  /** Open a section INSIDE Navigate (2026-10-05, app-study.html). The search
   *  clears, so the section is what the person sees. Pre-flight with no result
   *  yet runs one, so the pane is never empty. */
  /** Open Help at a view (a form, My reports), inside Navigate. */
  function helpAt(view: HelpView): void {
    openPane('help');
    helpGo(view);
  }
  function openPane(p: NavPane): void {
    setQuery('');
    if (searchRef) searchRef.value = '';
    if (p === 'preflight' && !lastResult() && !preflightRunning()) void runAndStore();
    showPane(p);
    // Docked on the canvas (DEC-268), a section with no open switch of its own
    // (Home, Add a control, Timelines, Matrix, the project) has nothing else
    // to open Navigate over the canvas, so the row would only light up.
    if (navigateDocked() && !navigateOpen()) openNavigateAt(p);
  }
  const pane = navigatePane;
  // A section opens at its top. The list and the section share one scroller
  // on a phone (.logo-menu-main) and the section has its own beside the list
  // wider up (.logo-menu-body); without this, a row tapped low in the list
  // opened its section scrolled to wherever the list had been.
  let mainEl: HTMLDivElement | undefined;
  let bodyEl: HTMLDivElement | undefined;
  createEffect(on(pane, () => { mainEl?.scrollTo(0, 0); bodyEl?.scrollTo(0, 0); }, { defer: true }));
  // The Project pane opens on its list; its "New project" action re-opens it
  // on the form (a fresh mount, since the browser reads its view once).
  const [projView, setProjView] = createSignal<'list' | 'new'>('list');
  const [projKey, setProjKey] = createSignal(0);

  /* ─────────────── connection (same sources as the header pill) ─────────────── */
  interface Conn { tone: Tone; title: string; meta: string; badge: Badge }
  const conn = createMemo((): Conn => {
    // Register a dependency on connection SWAPS before reading the map - the
    // OpbridgeBadge comment explains the 147-second "CONNECTED while offline"
    // it caused when this was missing.
    connectionsVersion();
    if (consumerRevoked()) return { tone: 'bad', title: 'Access revoked', meta: 'Re-pair this device to reconnect', badge: { text: 'revoked', tone: 'warn' } };
    if (consumerDenied()) return { tone: 'bad', title: 'Missing permission', meta: 'Re-pair this device to reconnect', badge: { text: 're-pair', tone: 'warn' } };
    const cfgs = bridges();
    if (cfgs.length === 0) {
      return { tone: 'warn', title: 'Not connected', meta: 'Connect to your computer to control Ableton', badge: { text: 'not connected', tone: 'warn' } };
    }
    if (aggregateStatus() === 'unpaired') {
      return { tone: 'warn', title: 'Needs pairing', meta: cfgs.map((c) => c.name).join(' · '), badge: { text: 'pair', tone: 'warn' } };
    }
    const rows = cfgs.map((c) => {
      const cn = getConnection(c.id);
      return { name: c.name, up: !!cn && cn.statusSignal()() === 'connected' };
    });
    const up = rows.filter((r) => r.up).length;
    if (up === rows.length) {
      return { tone: 'good', title: 'Connected', meta: rows.map((r) => r.name).join(' · '), badge: { text: `${up} of ${rows.length}`, dot: 'good' } };
    }
    if (up === 0) {
      return { tone: 'bad', title: 'OPBridge offline', meta: rows.map((r) => r.name).join(' · '), badge: { text: `0 of ${rows.length}`, dot: 'bad' } };
    }
    return {
      tone: 'warn',
      title: `${up} of ${rows.length} connected`,
      meta: rows.map((r) => (r.up ? r.name : `${r.name} (offline)`)).join(' · '),
      badge: { text: `${up} of ${rows.length}`, dot: 'good' },
    };
  });

  /* ─────────────── the status cards (app-study.html, 2026-10-05) ───────────────
     Computers, Pre-flight, Now playing: each says where things stand in words
     and has ONE button that goes to it. */
  const computers = createMemo((): { value: string; meta: string } => {
    connectionsVersion();
    if (consumerRevoked()) return { value: 'Access revoked', meta: 'Re-pair this device to reconnect.' };
    if (consumerDenied()) return { value: 'Missing permission', meta: 'Re-pair this device to reconnect.' };
    const cfgs = bridges();
    if (cfgs.length === 0) return { value: 'Not connected', meta: 'Pair with OPBridge to control Ableton and your rig.' };
    if (aggregateStatus() === 'unpaired') return { value: 'Needs pairing', meta: `${cfgs.map((c) => c.name).join(' and ')}.` };
    const up = cfgs.filter((c) => getConnection(c.id)?.statusSignal()() === 'connected').map((c) => c.name);
    const down = cfgs.filter((c) => !up.includes(c.name)).map((c) => c.name);
    if (up.length === 0) return { value: 'Not answering', meta: `${down.join(' and ')} ${down.length === 1 ? 'is' : 'are'} offline.` };
    return {
      value: up.length === 1 ? `${up[0]} connected` : `${up.length} computers connected`,
      meta: down.length ? `${down.join(' and ')} ${down.length === 1 ? 'is' : 'are'} offline.` : up.length > 1 ? `${up.join(' and ')}.` : 'All set.',
    };
  });

  const preflightCard = createMemo((): { value: string; meta: string } => {
    if (preflightRunning()) return { value: 'Checking...', meta: 'Running every check now.' };
    const r = lastResult();
    if (!r) return { value: 'Not checked yet', meta: 'Check the rig before a show.' };
    const warn = r.rows.filter((x) => x.status === 'warn');
    const fail = r.rows.filter((x) => x.status === 'fail');
    const first = fail[0] ?? warn[0];
    const value = fail.length ? `${fail.length} ${fail.length === 1 ? 'problem' : 'problems'}`
      : warn.length ? `Ready, ${warn.length} ${warn.length === 1 ? 'warning' : 'warnings'}`
      : 'Ready';
    return { value, meta: first ? `${first.label}: ${first.detail}` : `Everything checked out at ${hhmm(r.ranAtMs)}.` };
  });

  // Now playing: the Song any computer is running, from the timeline transport every playhead reads (2026-10-16: it
  // read its own 1-5 s state poll, and called a start still waiting for its bar line "playing"). Followed only while
  // Navigate is open; docked on a desktop (DEC-268) Navigate stays mounted while the canvas shows, so this follows
  // "a section is showing" rather than the mount. The bar is re-read twice a second while something runs.
  createEffect(() => {
    if (!navigateOpen()) return;
    const offs = untrack(bridges).map((b) => followTransport(b.id));
    onCleanup(() => offs.forEach((off) => off()));
  });
  const [navTick, setNavTick] = createSignal(0);
  createEffect(() => {
    if (!navigateOpen() || !bridges().some((b) => transportNow(b.id))) return;
    const iv = setInterval(() => setNavTick((n) => n + 1), 500);
    onCleanup(() => clearInterval(iv));
  });
  const nowPlaying = createMemo((): { value: string; meta: string } | null => {
    navTick();
    for (const b of bridges()) {
      const now = transportNow(b.id);
      if (!now) continue;
      const t = allTimelines().find((x) => x.id === now.timelineId);
      const total = t ? timelineTotalBars(t) : 0;
      if (now.waiting) return { value: t?.name ?? 'A timeline', meta: `Starting at the next bar line, on ${b.name}.` };
      return { value: t?.name ?? 'A timeline', meta: total ? `Bar ${Math.min(now.bar, total)} of ${total}, on ${b.name}.` : `On ${b.name}.` };
    }
    return null;
  });

  /* ─────────────── project ─────────────── */
  const controlCount = createMemo(() => (activeProject()?.pages ?? []).reduce((n, p) => n + p.items.length, 0));

  /* ─────────────── get set up (novice floor) ─────────────── */
  // Only while something is genuinely not set up on THIS device. A paired rig
  // that is merely offline right now is a status (the Connection card), not
  // an onboarding step.
  const setupSteps = createMemo(() => [
    { id: 'connect', title: 'Connect to your computer', done: bridges().length > 0, run: () => openPane('connect') },
    { id: 'add', title: 'Add your first control', done: controlCount() > 0, run: () => openPane('add') },
  ]);
  const showSetup = createMemo(() => !setupHidden() && setupSteps().some((s) => !s.done));

  /* ─────────────── the map: ONE list drives the cards and the search ─────────────── */
  // Names, order and counts follow app-study.html's Studio list (operator,
  // 2026-10-05: "do them all"): a plain count when there is something, nothing
  // when there is not. "Computers" replaces the operator's 2026-09-30 "Connect"
  // for this row, as the study has it; the popup it opens keeps its own name.
  const preflightIssues = createMemo(() => (lastResult()?.rows ?? []).filter((r) => r.status === 'warn' || r.status === 'fail').length);
  const audioCount = () => audioTracks().length;
  const groups = createMemo((): Group[] => {
    const pages = activeProject()?.pages.length ?? 0;
    const tl = timelinesInActiveProject().length; // this project's (DEC-235)
    const banks = rigStateBanks().length;
    const pedals = activeProject()?.pedals?.length ?? 0;
    const lights = fixtures().length;
    const mDevices = matrixLibrary().devices.length;
    const count = (n: number): Badge | null => (n > 0 ? { text: String(n) } : null);
    const skinName = SKINS.find((k) => k.id === activeSkin())?.name ?? '';
    const issues = preflightIssues();
    return [
      {
        id: 'build', title: 'Project', cards: [
          { id: 'pages', cls: 'logo-menu-pages', icon: 'pages', title: 'Pages', detail: 'Add, rename and reorder the pages of this project',
            badge: count(pages),
            keywords: 'page pages tab tabs screen rename reorder colour color', open: () => openPane('pages') },
          { id: 'timelines', cls: 'logo-menu-timelines', icon: 'timelines', title: 'Timelines', detail: 'Play actions in time - builds, drops, song cues',
            badge: count(tl),
            keywords: 'timeline timelines arrange automation song cue sequence clip lane genre', open: () => openPane('timelines') },
          { id: 'audio', cls: 'logo-menu-audio', icon: 'audio', title: 'Audio', detail: 'Songs, loops and one-shots - import, mark and manage',
            badge: audioCount() > 0 ? { text: String(audioCount()) } : null,
            keywords: 'audio sound sounds song songs loop loops one-shot oneshot sample samples mp3 wav import file files track tracks music', open: () => openPane('audio') },
          { id: 'rigstates', cls: 'logo-menu-rigstates', icon: 'rigState', title: 'Rig States', detail: 'Save a whole setup and recall it with one press',
            badge: count(banks),
            keywords: 'rig state states snapshot scene preset recall save setup look bank', open: () => openPane('rigstates') },
          { id: 'add', cls: 'logo-menu-add', icon: 'add', title: 'Add a control', detail: 'Buttons, faders, pads - drop one on the page',
            keywords: 'add new button fader knob pad control item palette widget insert', open: () => openPane('add') },
        ],
      },
      {
        id: 'rig', title: 'Rig', cards: [
          { id: 'connect', cls: 'logo-menu-connect', icon: 'computer', title: 'Computers', detail: 'Pair with OPBridge on your Mac or PC',
            badge: conn().badge,
            keywords: 'computers computer connect pair pairing opbridge bridge mac pc laptop qr network wifi link', open: () => openPane('connect') },
          { id: 'devices', cls: 'logo-menu-connected-devices', icon: 'pedal', title: 'Pedals and devices', detail: 'Your guitar pedals - Helix and more',
            badge: count(pedals),
            keywords: 'pedals devices device pedal guitar fx effects helix line6 kemper amp connected', open: () => openPane('devices') },
          { id: 'lights', cls: 'logo-menu-lights', icon: 'lights', title: 'Lights', detail: 'Add your lights, patch and group them',
            badge: count(lights),
            keywords: 'light lights dmx fixture fixtures lamp par wash beam group patch lighting', open: () => openPane('lights') },
          { id: 'matrix', cls: 'logo-menu-matrix', icon: 'matrix', title: 'MIDI matrix', detail: 'Every MIDI channel, CC and note in use across the rig',
            badge: count(mDevices),
            keywords: 'matrix midi map cc note channel program change pc claim mapping conflict clash', open: () => openPane('matrix') },
          { id: 'preflight', cls: 'logo-menu-preflight', icon: 'preflight', title: 'Pre-flight', detail: 'Check the rig before a show',
            badge: issues > 0 ? { text: String(issues), tone: 'warn' } : null,
            keywords: 'preflight pre-flight check ready gig test rig soundcheck', open: () => openPane('preflight') },
        ],
      },
      {
        id: 'app', title: 'App', cards: [
          // THE PROJECT LIST, its own row (2026-10-16, operator: "the project
          // picker should be separated to the actual project that is
          // loaded"). The card at the top is the project you are in.
          { id: 'project', cls: 'logo-menu-projects-row', icon: 'projects', title: 'Projects', detail: 'Open, start or import a project',
            badge: count(projects().length),
            keywords: 'projects project open switch load browse new import all set show', open: () => openPane('project') },
          { id: 'look', cls: 'logo-menu-look', icon: 'look', title: 'Look', detail: 'Skins, bar position and screen dim',
            badge: skinName ? { text: skinName } : null,
            keywords: 'look skin skins theme colour color appearance dim brightness dark', open: () => openPane('look') },
          { id: 'backup', cls: 'logo-menu-backup', icon: 'backup', title: 'Backup', detail: 'Export or restore everything',
            keywords: 'backup export import restore save file download', open: () => openPane('backup') },
          { id: 'settings', cls: 'logo-menu-settings', icon: 'settings', title: 'Settings', detail: 'Connection, help, diagnostics and more',
            keywords: 'settings preferences options config', open: () => openPane('settings') },
        ],
      },
    ];
  });

  /* ─────────────── the home view (2026-10-05) ───────────────
     The destinations are the list on the left; this side offers the things
     people open Navigate to DO, as verbs, and the pages to go to. */
  interface Action { id: string; icon: IconName; title: string; detail: string; run: () => void }
  // The study's six, in its order (2026-10-05). "Edit this page" left this row
  // for "Connect a computer"; editing stays one tap away on the bar's pencil,
  // and "Switch to Edit" in this search.
  const actions = createMemo((): Action[] => [
    { id: 'add', icon: 'add', title: 'Add a control', detail: 'Buttons, faders, pads', run: () => openPane('add') },
    { id: 'page', icon: 'pages', title: 'Add a page', detail: 'A fresh page in this project', run: () => { addPage(); openPane('pages'); } },
    // Says which kind it makes (2026-10-16: "New timeline" always made a Song; an
    // Action timeline is Timelines' second button).
    { id: 'timeline', icon: 'timelines', title: 'New song timeline', detail: 'Sections, launched from a pad',
      run: () => go(() => openTimelineEditor(createTimeline('song', 'New song').id)) },
    { id: 'lighting-timeline', icon: 'lights', title: 'New lighting timeline', detail: 'Your lights only, or the default lights between songs',
      run: () => go(() => openTimelineEditor(createLightingTimeline('New lighting').id)) },
    { id: 'light', icon: 'lights', title: 'Add a light', detail: 'Patch a light into your rig', run: () => openPane('lights') },
    { id: 'connect', icon: 'computer', title: 'Connect a computer', detail: 'Pair with OPBridge', run: () => openPane('connect') },
    { id: 'preflight', icon: 'preflight', title: 'Run pre-flight', detail: 'Check the rig before a show',
      run: () => { void runAndStore(); openPane('preflight'); } },
  ]);
  /** Every page of the project in miniature, drawn from its real controls -
   *  where each one sits and its colour - so a page is known by its shape.
   *  A tap goes to it. */
  const thumbs = createMemo(() => {
    const p = activeProject();
    if (!p) return [];
    const frame = canvasSizeOf(p);
    const cur = activePage()?.id;
    return p.pages.map((pg, i) => ({
      id: pg.id, n: i + 1, name: pg.name, count: pg.items.length, current: pg.id === cur,
      hue: pg.color ?? 'sky',
      aspect: `${frame.w} / ${frame.h}`,
      boxes: pg.items.slice(0, 80).map((it) => {
        const hue = hueOf(it);
        return {
          left: `${(it.x / frame.w) * 100}%`,
          top: `${(it.y / frame.h) * 100}%`,
          width: `${Math.max(1.2, (it.w / frame.w) * 100)}%`,
          height: `${Math.max(1.6, (it.h / frame.h) * 100)}%`,
          color: hue === 'none' ? '#8a9097' : HUE_VALUES[hue],
        };
      }),
    }));
  });

  /* ─────────────── search index (the ceiling) ─────────────── */
  interface Runnable extends SearchEntry { icon: IconName; run: () => void }
  // `where` is the Navigate section that holds it: Settings split into Look,
  // Pre-flight, Backup and Settings (2026-10-05, app-study.html).
  const SECTIONS: Array<{ section: SettingsSection; title: string; detail: string; keywords: string; where: string }> = [
    // Not titled "Connect": Computers already is, and it holds the pairing
    // page. What this section adds is the drop history (the black box).
    { section: 'connection', title: 'Connection history', detail: 'The last dropped connection, and every one before it', keywords: 'connect incident black box drop disconnect lost history log', where: 'Settings' },
    { section: 'diagnostics', title: 'Diagnostics', detail: 'Project sync, offline path, certificate', keywords: 'diagnostics debug sync certificate offline', where: 'Settings' },
    { section: 'interface', title: 'Interface', detail: 'Bar position, triggers in edit mode, screen dim', keywords: 'interface skin bar position dim brightness', where: 'Look' },
    { section: 'preflight', title: 'Pre-flight thresholds', detail: 'Latency, battery and clock limits', keywords: 'preflight pre-flight threshold latency battery', where: 'Pre-flight' },
    { section: 'panic', title: 'Panic', detail: 'Stop every stuck note and everything running', keywords: 'panic stop kill silence emergency stuck notes all off', where: 'Settings' },
    { section: 'panic-midi-trigger', title: 'Panic MIDI trigger', detail: 'Fire PANIC from a MIDI pedal or pad', keywords: 'panic trigger midi foot switch pedal learn', where: 'Settings' },
    { section: 'export', title: 'Export', detail: 'Save a project, the matrix or everything to a file', keywords: 'export backup save download csv', where: 'Backup' },
    { section: 'import', title: 'Import', detail: 'Bring a project, matrix or backup in from a file', keywords: 'import restore upload touchosc tosc', where: 'Backup' },
    { section: 'about', title: 'About', detail: 'Which build this is, and this screen in numbers', keywords: 'about build version update sha details screen frame', where: 'Settings' },
  ];
  const index = createMemo((): Runnable[] => {
    const out: Runnable[] = [];
    for (const g of groups()) {
      for (const c of g.cards) {
        out.push({ id: `dest:${c.id}`, kind: 'dest', title: c.title, detail: c.detail, where: g.title, keywords: c.keywords, icon: c.icon, run: c.open });
      }
    }
    out.push({ id: 'dest:help', kind: 'dest', title: 'Getting started', detail: 'What OPButtons is and what it needs', where: 'App', icon: 'help',
      keywords: 'tour guide start learn basics tutorial welcome', run: () => go(props.onGettingStarted) });
    out.push({ id: 'dest:helpdesk', kind: 'dest', title: 'Help', detail: 'Answers to "how do I...", in your words', where: 'App', icon: 'help',
      keywords: 'help how question answer manual docs support faq', run: () => openPane('help') });
    out.push({ id: 'dest:report', kind: 'dest', title: 'Report a problem', detail: 'Something froze or did the wrong thing', where: 'Help', icon: 'help',
      keywords: 'report problem bug broken crash froze freeze stuck issue wrong contact support', run: () => helpAt({ v: 'report', type: 'bug' }) });
    out.push({ id: 'dest:idea', kind: 'dest', title: 'Suggest an idea', detail: 'Something you wish OPButtons did', where: 'Help', icon: 'help',
      keywords: 'idea suggest suggestion feature request wish feedback would like', run: () => helpAt({ v: 'report', type: 'idea' }) });
    out.push({ id: 'dest:question', kind: 'dest', title: 'Ask a question', detail: 'The answer comes back in My reports', where: 'Help', icon: 'help',
      keywords: 'ask question how why contact support', run: () => helpAt({ v: 'report', type: 'question' }) });
    out.push({ id: 'dest:myreports', kind: 'dest', title: 'My reports', detail: 'Replies and what happened to what you sent', where: 'App', icon: 'help',
      keywords: 'my reports replies answer status sent tickets', run: () => helpAt({ v: 'reports' }) });
    out.push({ id: 'dest:projects', kind: 'dest', title: 'Switch project', detail: 'Open another project', where: 'Project', icon: 'project',
      keywords: 'open switch project projects load browse all', run: () => openPane('project') });
    out.push({ id: 'dest:new', kind: 'dest', title: 'New project', detail: 'Start a fresh layout', where: 'Project', icon: 'newProject',
      keywords: 'new project create start fresh blank layout', run: () => go(props.onNewProject) });
    out.push({ id: 'act:perf', kind: 'action', title: performanceMode() ? 'Turn performance mode off' : 'Turn performance mode on',
      detail: 'Every OP app switches with it', where: 'Show', icon: 'perf', keywords: 'performance mode stage live gig show perf',
      run: () => setPerformanceMode(!performanceMode()) });
    // "connect" is what people type to pair a computer: the study's verb is an
    // action here, so it outranks "Connection history" (a Settings section).
    out.push({ id: 'act:connect', kind: 'action', title: 'Connect a computer', detail: 'Pair with OPBridge', where: 'Rig', icon: 'computer',
      keywords: 'connect pair pairing computer computers opbridge bridge mac pc laptop qr', run: () => openPane('connect') });
    out.push({ id: 'act:preflight', kind: 'action', title: 'Run pre-flight', detail: 'Check the rig now and show the result', where: 'Show', icon: 'preflight',
      keywords: 'preflight pre-flight check ready gig test rig', run: () => { openPane('preflight'); if (!preflightRunning()) void runAndStore(); } });
    out.push({ id: 'act:edit', kind: 'action', title: isEditMode() ? 'Switch to Test' : 'Switch to Edit',
      detail: isEditMode() ? 'Stop editing and play the page' : 'Move, resize and set up controls', where: 'Page', icon: 'edit',
      keywords: 'edit test mode play perform arrange move', run: () => go(toggleEditMode) });
    for (const s of SECTIONS) {
      out.push({ id: `sec:${s.section}`, kind: 'section', title: s.title, detail: s.detail, where: s.where, icon: 'settings',
        keywords: s.keywords, run: () => { setQuery(''); if (searchRef) searchRef.value = ''; openSettings(s.section); } });
    }
    out.push({ id: 'tool:traffic', kind: 'tool', title: 'Traffic Monitor', detail: 'Watch the MIDI and OSC OPBridge receives, live', where: 'Settings',
      icon: 'traffic', keywords: 'traffic monitor midi osc log incoming debug', run: () => go(openTrafficMonitor) });
    out.push({ id: 'tool:skin', kind: 'tool', title: 'Skin', detail: 'Pick one of seven looks', where: 'Look', icon: 'look',
      keywords: 'skin theme look colour color', run: () => openPane('look') });
    out.push({ id: 'tool:macros', kind: 'tool', title: 'Macros', detail: 'Edit the macro library', where: 'Project', icon: 'macros',
      keywords: 'macro macros sequence automation library', run: () => go(openMacrosEditor) });
    const cur = activeProjectId();
    for (const p of projects()) {
      if (p.id === cur) continue;
      out.push({ id: `project:${p.id}`, kind: 'project', title: p.name, detail: 'Switch to this project', where: 'Project', icon: 'project',
        keywords: 'project', run: () => go(() => switchProject(p.id)) });
    }
    for (const t of timelinesInActiveProject()) {
      out.push({ id: `timeline:${t.id}`, kind: 'timeline', title: t.name, detail: 'Open this timeline', where: 'Timeline', icon: 'timelines',
        keywords: 'timeline', run: () => go(() => openTimelineEditor(t.id)) });
    }
    (activeProject()?.pages ?? []).forEach((pg, i) => {
      out.push({ id: `page:${pg.id}`, kind: 'page', title: pg.name, detail: `Go to page ${i + 1}`, where: 'Page', icon: 'page',
        keywords: `page ${i + 1}`, run: () => go(() => switchPage(pg.id)) });
    });
    return out;
  });
  const results = createMemo(() => rankEntries(index(), query()) as Runnable[]);
  createEffect(() => { query(); setActive(0); });

  function runActive(): void {
    const r = results()[active()];
    if (r) r.run();
  }

  // The dot on "?" is looked at here, so check for replies as Navigate opens
  // (docked: on mount, and each time a section opens).
  createEffect(() => { if (navigateOpen() || navigateDocked()) untrack(refreshUnreadSoon); });

  /* ─────────────── keyboard ─────────────── */
  // The browser's back (navigate/backButton.ts): the account menu, then the search, as Esc; the section and Navigate
  // itself are backButton's own steps.
  onCleanup(onBack(() => {
    if (!navigateOpen()) return false;
    if (opidOpen()) { setOpidOpen(false); return true; }
    if (query()) { setQuery(''); if (searchRef) searchRef.value = ''; return true; }
    return false;
  }));
  function onKey(e: KeyboardEvent): void {
    if (!navigateOpen()) return;
    // Docked, \ is the logo's key (App hides and shows Navigate with it).
    if (e.key === '\\' && navigateDocked()) return;
    if (e.key === 'Escape') {
      // A window opened from inside a section (Help, Traffic Monitor, a
      // confirm, the QR scanner) sits in Navigate's portal and closes itself
      // on Escape, and a section's own inner step (inside a Rig States bank,
      // the pedal model list) is marked data-nav-layer and steps back itself.
      // Navigate waits for the next press. This listener is on the CAPTURE
      // phase so it reads that layer before its own handler removes it,
      // whatever order the two were mounted in.
      if (portalEl?.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      if (portalEl?.parentElement?.querySelector('[data-nav-layer]')) return;
      // One layer per press: the account menu, then the search, then Navigate.
      if (opidOpen()) { setOpidOpen(false); return; }
      if (query()) { setQuery(''); if (searchRef) searchRef.value = ''; return; }
      closeNavigate();
      return;
    }
    if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey) && !e.altKey) {
      // Already open: the shortcut goes to the search, and a second press closes.
      e.preventDefault();
      if (document.activeElement === searchRef) closeNavigate();
      else searchRef?.focus();
      return;
    }
    if (searching() && document.activeElement === searchRef) {
      const n = results().length;
      if (e.key === 'ArrowDown' && n) { e.preventDefault(); setActive((a) => (a + 1) % n); return; }
      if (e.key === 'ArrowUp' && n) { e.preventDefault(); setActive((a) => (a - 1 + n) % n); return; }
      if (e.key === 'Enter' && n) { e.preventDefault(); runActive(); return; }
    }
    // Type anywhere: the first printable key lands in the search. Not while a sheet or a layer is over a section: its
    // keys are its own (2026-10-21: keys 1-5 naming a song part in the Audio parts sheet went into the search).
    if (portalEl?.querySelector('[role="dialog"], [role="alertdialog"]') || portalEl?.parentElement?.querySelector('[data-nav-layer]')) return;
    if (!isTextField(document.activeElement) && !e.metaKey && !e.ctrlKey && !e.altKey
        && e.key.length === 1 && /\S/.test(e.key)) {
      searchRef?.focus();
    }
  }
  function onDocPointerDown(e: PointerEvent): void {
    if (!opidOpen()) return;
    const el = e.target as HTMLElement | null;
    if (el && el.closest('.logo-menu-opid')) return;
    setOpidOpen(false);
  }
  onMount(() => {
    returnFocus = document.activeElement;
    window.addEventListener('keydown', onKey, { capture: true });
    window.addEventListener('pointerdown', onDocPointerDown);
    // Docked and showing the canvas, nothing takes focus: the keyboard is
    // the canvas's (arrow nudges, shortcuts) until a section opens.
    if (!navigateOpen()) return;
    const wantSearch = navigateWantsSearch();
    clearNavigateWantsSearch();
    if (wantSearch || !touchOnly) searchRef?.focus({ preventScroll: true });
  });
  // Docked, Navigate is already mounted when Ctrl/Cmd+K asks for the search.
  createEffect(on(navigateOpen, (open) => {
    if (!open || !navigateDocked() || !untrack(navigateWantsSearch)) return;
    clearNavigateWantsSearch();
    searchRef?.focus({ preventScroll: true });
  }, { defer: true }));
  onCleanup(() => {
    window.removeEventListener('keydown', onKey, { capture: true });
    window.removeEventListener('pointerdown', onDocPointerDown);
    // Hand focus back to what opened us (the logo), unless the destination
    // just took it - a modal that opened on close owns focus now.
    const back = returnFocus as HTMLElement | null;
    if (back && document.body.contains(back) && (document.activeElement === document.body || !document.activeElement)) {
      back.focus?.({ preventScroll: true });
    }
  });

  let portalEl: HTMLElement | undefined;
  onCleanup(() => setNavigatePortal(undefined));

  /** "Jump back in": a timeline opens in the editor; a control is selected on
   *  its page in Edit mode with the Inspector open; Lights opens here. */
  function jumpBack(a: Activity): void {
    if (a.kind === 'lights') { openPane('lights'); return; }
    if (a.kind === 'timeline') { go(() => openTimelineEditor(a.id)); return; }
    go(() => {
      if (a.pageId) switchPage(a.pageId);
      if (!isEditMode()) toggleEditMode();
      selectItem(a.id);
    });
  }

  /* ─────────────── the sections (app-study.html PANES) ───────────────
     Each one: a title, one plain line about what it is, its main action at
     the top right, then the section itself - the same screen its own window
     shows, rendered here. */
  interface PaneDef { title: string; desc: string; actions?: Array<{ label: string; primary?: boolean; run: () => void }> }
  const PANE_DEFS = (): Record<Exclude<NavPane, 'home'>, PaneDef> => ({
    project: { title: 'Projects', desc: 'A project is everything you play at one show: its pages, controls and timelines. Open another one here.',
      actions: [{ label: 'New project', run: () => { setProjView('new'); setProjKey((k) => k + 1); } }] },
    pages: { title: 'Pages', desc: `The pages of ${project()?.name ?? 'this project'}, in the order the top bar shows them. Drag to reorder.`,
      actions: [{ label: 'Add a page', primary: true, run: () => { addPage(); } }] },
    add: { title: 'Add a control', desc: 'Pick what you want it to do. It lands on the page you are on, ready to change.' },
    // New, Search and Sort live in the section's own tool row (version B of timelines-study.html, 2026-10-16).
    timelines: { title: 'Timelines', desc: 'Shows that play themselves in time, on the beat.' },
    audio: { title: 'Audio', desc: 'Every audio file you have added, on every computer in the rig. Drop files on One-shots, Loops or Songs.',
      actions: [{ label: 'Import audio...', primary: true, run: () => openAudioPicker() }] },
    rigstates: { title: 'Rig States', desc: 'Save how the whole rig is set, then bring it all back with one press.' },
    connect: { title: 'Computers', desc: 'OPButtons talks to OPBridge on each computer in your rig. Pair once and it stays paired.' },
    devices: { title: 'Pedals and devices', desc: 'Tell OPButtons once which pedal is where. Controls then pick it by name.' },
    lights: { title: 'Lights', desc: 'Your rig as OPBridge knows it. Group lights so one control can aim at all of them.' },
    matrix: { title: 'MIDI matrix', desc: 'Every MIDI channel, CC and note in use across the rig, and who uses it.',
      actions: [
        { label: 'Open the full matrix', primary: true, run: () => go(openMatrixViewer) },
        { label: 'Export CSV', run: () => downloadBlob(buildMatrixCsv(matrixLibrary()), `opbuttons-matrix-${timestampForFilename()}.csv`, 'text/csv') },
      ] },
    look: { title: 'Look', desc: 'How the stage looks on this device. Saved here only, so the iPad and the laptop can differ.' },
    preflight: { title: 'Pre-flight', desc: 'Checks the rig before a show. It also runs when you turn on performance mode.',
      actions: [{ label: preflightRunning() ? 'Checking...' : 'Run again', primary: true, run: () => { void runAndStore(); } }] },
    backup: { title: 'Backup', desc: 'Everything in one file, kept wherever you save it. Restore puts it back.' },
    settings: { title: 'Settings', desc: 'How OPButtons behaves on this device.' },
    // Help's head follows the view on top (an article's title, "Suggest an
    // idea", My reports); its back link walks the view stack (paneView).
    help: helpHead(),
  });
  function paneBody(p: Exclude<NavPane, 'home'>): JSX.Element {
    switch (p) {
      case 'project':
        return (
          <Show when={projKey() + 1} keyed>
            <ProjectBrowser
              embedded
              dismissible
              onClose={closeNavigate}
              initialView={projView()}
              onConnect={() => openPane('connect')}
            />
          </Show>
        );
      case 'pages': return <PageManager />;
      case 'add': return <ItemPalette embedded onClose={() => { if (!isEditMode()) toggleEditMode(); closeNavigate(); }} />;
      case 'timelines': return <TimelinesPane onOpen={(id) => go(() => openTimelineEditor(id))}
        onNew={(tier) => go(() => openTimelineEditor((tier === 'lights' ? createLightingTimeline('New lighting') : createTimeline(tier, tier === 'song' ? 'New song' : 'New action')).id))} />;
      case 'audio': return <AudioPane />;
      case 'rigstates': return <RigStateManager />;
      case 'connect': return <div class="nav-pane-body settings-body" role="region" aria-label="Computers"><ConnectPage /></div>;
      case 'devices': return <ConnectedDevices />;
      case 'lights': return <LightsManager />;
      case 'matrix': return <MatrixPane />;
      case 'look': return <><SkinPicker /><SettingsPanel sections={['interface']} /></>;
      case 'preflight': return <><PreflightPanel /><SettingsPanel sections={['preflight']} /></>;
      case 'backup': return <SettingsPanel sections={['export', 'import']} />;
      case 'settings': return <SettingsPanel sections={['panic', 'panic-midi-trigger', 'connection', 'help', 'diagnostics', 'about']} />;
      case 'help': return <HelpPane />;
    }
  }
  /** One section. Built ONCE per section id: only its header text follows
   *  the stores. It is rendered as a <Show> fallback, which runs inside
   *  Show's memo, so a store read here at build time (the project name in the
   *  Pages line, "Checking..." on Pre-flight) re-ran that memo and rebuilt
   *  the whole section - a page rename lost focus after one letter. The
   *  keyed Show tracks the id alone, the header reads stay in its own JSX
   *  bindings, and the body is built untracked. */
  function paneView(): JSX.Element {
    return (
      <Show when={pane() as Exclude<NavPane, 'home'>} keyed>
        {(p) => {
          const d = () => PANE_DEFS()[p];
          return (
            <section class="logo-menu-pane" data-pane={p} aria-label={d().title}>
              {/* On a phone the list and the section take turns; this is the way
                  back to the list (the study's "Everything"). */}
              {/* Inside Help the one back link walks Help's own views, at every
                  width ("‹ My reports"); at Help's home it is the phone's
                  "‹ Everything" again. One way back, never two. */}
              <Show
                when={p === 'help' && helpPrevView()}
                fallback={<button type="button" class="logo-menu-pane-back" onClick={() => openPane('home')}>‹ Everything</button>}
              >
                {(prev) => <button type="button" class="logo-menu-pane-back nv-back-always" onClick={helpBack}>‹ {headOf(prev()).title}</button>}
              </Show>
              <div class="logo-menu-pane-head logo-menu-pane-head-row">
                <div class="logo-menu-pane-head-text">
                  <h1 class="logo-menu-pane-title">{d().title}</h1>
                  <p class="logo-menu-pane-desc">{d().desc}</p>
                </div>
                <Show when={d().actions?.length}>
                  <div class="logo-menu-pane-actions">
                    <Index each={d().actions ?? []}>
                      {(a) => <button type="button" class="logo-menu-pane-btn" data-primary={a().primary ? 'true' : undefined} onClick={() => a().run()}>{a().label}</button>}
                    </Index>
                  </div>
                </Show>
              </div>
              {untrack(() => paneBody(p))}
            </section>
          );
        }}
      </Show>
    );
  }

  /* ─────────────── render helpers ─────────────── */
  function BadgeView(p: { b: Badge | null | undefined }) {
    return (
      <Show when={p.b}>
        {(b) => (
          <span class="logo-menu-badge" data-tone={b().tone} data-dot={b().dot}>
            <Show when={b().dot}><i class="logo-menu-badge-dot" aria-hidden="true" /></Show>
            {b().text}
          </span>
        )}
      </Show>
    );
  }
  function Title(p: { text: string }) {
    const range = () => matchRange(p.text, query());
    return (
      <Show when={range()} fallback={p.text}>
        {(r) => <>{p.text.slice(0, r()[0])}<mark>{p.text.slice(r()[0], r()[1])}</mark>{p.text.slice(r()[1])}</>}
      </Show>
    );
  }
  const project = activeProject;

  return (
    <Portal mount={document.body}>
      {/* `skin` + data-skin: this layer is outside `.shell`, where the skin
          tokens live; PANIC and the performance switch read them. The rest is
          fixed chrome (DEC-170), in the timeline editor's values (DEC-239). */}
      <div
        class="navigate-layer skin"
        data-skin={activeSkin()}
        data-docked={navigateDocked() ? 'true' : undefined}
        data-open={navigateOpen() ? 'true' : 'false'}
        data-chrome={navigateDocked() && dockHidden() ? 'hidden' : 'shown'}
      >
        {/* Docked there is nothing to dismiss into: the canvas IS a section. */}
        <Show when={!navigateDocked()}>
          <div class="sk-scrim navigate-scrim" onClick={closeNavigate} />
        </Show>
        <div
          class="sk-modal logo-menu-modal"
          data-size="full"
          data-searching={searching() ? 'true' : 'false'}
          data-touch={touchOnly ? 'true' : 'false'}
          data-pane={pane()}
          role="dialog"
          data-back-surface
          aria-modal="true"
          aria-label="Navigate"
        >
          <header class="sk-mhead logo-menu-head">
            {/* The OPButtons logo, as in app-study.html's Studio header (2026-10-05:
                the operator spotted the word "Navigate" here instead; the rebuild
                had dropped it). The same fixed two-tone PNG as the bar's
                wordmark, so both read as one app. The dialog keeps its
                "Navigate" name for screen readers. */}
            <Show
              when={navigateDocked()}
              fallback={<span class="sk-mtitle logo-menu-brand"><img src="/logo/opbuttons-logo.png" alt="OPButtons" /></span>}
            >
              {/* Docked (DEC-268): the logo hides and shows Navigate, as the
                  canvas bar's does - only while the Canvas section is
                  showing (2026-10-15); over another section it is a
                  picture. */}
              <button type="button" class="sk-mtitle logo-menu-brand logo-menu-brand-btn" onClick={() => setDockHidden(true)}
                disabled={navigateOpen()}
                title={navigateOpen() ? 'OPButtons' : 'Hide Navigate (\\)'} aria-label={navigateOpen() ? 'OPButtons' : 'Hide Navigate'}>
                <img src="/logo/opbuttons-logo.png" alt="OPButtons" />
              </button>
            </Show>
            <label class="logo-menu-search" data-has-query={searching() ? 'true' : 'false'}>
              <span class="logo-menu-search-mag">{ICONS.search()}</span>
              <input
                ref={searchRef}
                class="logo-menu-search-input"
                type="search"
                autocomplete="off"
                spellcheck={false}
                placeholder="What do you want to do?"
                aria-label="Search Navigate"
                role="combobox"
                aria-expanded={searching()}
                aria-controls="logo-menu-results"
                value={query()}
                onInput={(e) => {
                  setQuery(e.currentTarget.value);
                  // Docked on the canvas: the results need the section area.
                  if (navigateDocked() && !navigateOpen() && e.currentTarget.value.trim()) { setNavigatePane('home'); openNavigate(); }
                }}
              />
              <kbd class="logo-menu-search-kbd" aria-hidden="true">{/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</kbd>
              <button
                type="button"
                class="logo-menu-search-clear"
                aria-label="Clear search"
                onClick={(e) => { e.preventDefault(); setQuery(''); searchRef?.focus(); }}
              >
                ×
              </button>
            </label>
            {/* PANIC in the top bar only when this device asks for it
                (2026-10-07, operator: "maybe that could be in settings?
                default it to off"). Its home is Settings > Panic. It does NOT
                close Navigate - a kill switch you may need twice, and whose
                firing flash you need to see. */}
            {/* PERFORMANCE MODE, IN THE TOP BAR (2026-10-16, operator: "i think
                performance mode should be on the top bar not the left"; it was
                the top of the left column from 2026-10-07, DEC-258). Still the
                top of Navigate (DEC-216): one switch, held by OPBridge,
                switching every OP app. A label and a small switch, the whole
                thing one button; ON lights it in the logo's blue. Its status
                line shows only when it says something beyond the default - who
                switched it, an offline press, a refusal - and the smokes read
                it there. On a phone the label gives way to the icon. */}
            <div class="logo-menu-perf" data-on={performanceMode() ? 'true' : 'false'}>
              <button
                type="button"
                class="logo-menu-perf-toggle"
                role="switch"
                aria-checked={performanceMode()}
                aria-label="Performance mode"
                title={perfStatusLine()}
                onClick={() => setPerformanceMode(!performanceMode())}
              >
                <span class="logo-menu-tile-icon">{ICONS.perf()}</span>
                <span class="logo-menu-perf-text">
                  <span class="logo-menu-perf-label">Performance mode</span>
                  <Show when={perfStatusLine() !== PERF_DEFAULT_LINE}>
                    <span class="logo-menu-perf-meta" data-state={performanceModeProblem() ? 'bad' : undefined}>{perfStatusLine()}</span>
                  </Show>
                </span>
                <span class="logo-menu-perf-switch" aria-hidden="true" />
              </button>
            </div>
            <Show when={panicInTopBar()}>
              <PanicButton />
            </Show>
            {/* Help, one tap from anywhere in Navigate (2026-10-05: was the
                welcome view, which is still "What is OPButtons?" in Get set up
                and the "Getting started" search result). Left of the account
                chip: DEC-225 keeps the email beside PANIC. The dot is an
                unread reply in My reports. */}
            <button
              type="button"
              class="logo-menu-help-btn"
              aria-label={myReportsUnread() > 0 ? `Help - ${myReportsUnread()} new in My reports` : 'Help'}
              title="Help: answers, report a problem, my reports"
              aria-pressed={pane() === 'help' ? 'true' : 'false'}
              onClick={() => openPane('help')}
            >
              {ICONS.help()}
              <Show when={myReportsUnread() > 0}><span class="logo-menu-help-dot" aria-hidden="true" /></Show>
            </button>
            {/* OP ID account chip (DEC-170). In the header, where OPBridge's
                own chip is; on a narrow sheet it collapses to the dot and an
                initial. Dropdown content unchanged. */}
            <Show when={authIdentity()}>
              {(id) => (
                <div class="logo-menu-opid">
                  <button
                    type="button"
                    class="logo-menu-opid-trigger"
                    aria-haspopup="menu"
                    aria-expanded={opidOpen()}
                    aria-label={`Account: ${id().email ?? id().username ?? 'OP ID'}`}
                    onClick={() => setOpidOpen((v) => !v)}
                  >
                    <span class="logo-menu-opid-dot" aria-hidden="true" />
                    <span class="logo-menu-opid-initial" aria-hidden="true">
                      {(id().username ?? id().email ?? '?').slice(0, 1).toUpperCase()}
                    </span>
                    <span class="logo-menu-opid-email">{id().email ?? id().username ?? 'OP ID'}</span>
                    <span class="logo-menu-opid-car" aria-hidden="true">▾</span>
                  </button>
                  <Show when={opidOpen()}>
                    <div class="logo-menu-opid-pop" role="menu">
                      <div class="logo-menu-opid-head">
                        <div class="logo-menu-opid-name">{id().username ?? id().email ?? 'OP ID account'}</div>
                        <span class="logo-menu-opid-tier">{id().tier}</span>
                      </div>
                      <a class="logo-menu-opid-item" href="https://overpoweredapps.net" target="_blank" rel="noopener noreferrer"
                        onClick={() => setOpidOpen(false)}>overpoweredapps.net</a>
                      <a class="logo-menu-opid-item" href="https://accounts.overpoweredapps.net" target="_blank" rel="noopener noreferrer"
                        onClick={() => setOpidOpen(false)}>Account</a>
                      <button type="button" class="logo-menu-opid-item" onClick={() => { setOpidOpen(false); openPane('settings'); }}>Settings</button>
                      <button type="button" class="logo-menu-opid-item" onClick={() => { void refreshIdentity(); setOpidOpen(false); }}>
                        Refresh identity
                      </button>
                      <div class="logo-menu-opid-sep" />
                      <button
                        type="button"
                        class="logo-menu-opid-item danger"
                        onClick={() => {
                          if (!window.confirm('Sign out of OP ID on this device?')) return;
                          void signOutOpId();
                          closeNavigate();
                        }}
                      >
                        Log out
                      </button>
                    </div>
                  </Show>
                </div>
              )}
            </Show>
            {/* "Back to the stage" (app-study.html): the way out, named for
                where it goes. Esc does the same. */}
            {/* Docked, the Canvas row in the sidebar is the way back (DEC-268). */}
            <Show when={!navigateDocked()}>
              <button type="button" class="sk-mclose logo-menu-close" onClick={closeNavigate} title="Back to the stage (Esc)" aria-label="Back to the stage">
                <span class="logo-menu-close-icon" aria-hidden="true">{ICONS.stage()}</span>
                <span class="logo-menu-close-label">Back to the stage</span>
              </button>
            </Show>
          </header>

          <div class="logo-menu-main" ref={(el) => (mainEl = el)}>
            {/* THE MAP: every destination, always on screen, one tap each.
                Each opens its own surface directly (Navigate closes first). */}
            <div class="logo-menu-side-top">
              {/* THE PROJECT LEADS (2026-10-16, operator: "the top tier would be
                  'projects'... it gets a bit lost to performance mode's look";
                  mock: public/nav-sidebar-study.html, option B). The project
                  you are in, the biggest thing in the column; it opens its
                  Overview. Picking another project is the Projects row. */}
              <button
                type="button"
                class="logo-menu-project"
                aria-label={`Current project: ${project()?.name ?? 'none'}. Open its overview`}
                onClick={() => openPane('home')}
              >
                <span class="logo-menu-project-icon">{ICONS.project()}</span>
                <span class="logo-menu-project-body">
                  <span class="logo-menu-project-kicker">Project</span>
                  <span class="logo-menu-project-name">{project()?.name ?? 'No project'}</span>
                  <span class="logo-menu-project-meta">
                    {project()
                      ? `${plural(project()!.pages.length, 'page', 'pages')}, ${plural(controlCount(), 'control', 'controls')}`
                      : 'Tap to create one'}
                  </span>
                </span>
              </button>
            </div>
            <nav class="logo-menu-grid" aria-label="Destinations">
              {/* CANVAS, first in the list when docked (DEC-268): the controls,
                  in the main area beside this list. Picking it is what closing
                  Navigate means on a desktop. The page it is on, on the right. */}
              <Show when={navigateDocked()}>
                <button
                  type="button"
                  class="logo-menu-tile logo-menu-canvas"
                  data-dest="canvas"
                  title="Your controls (Esc)"
                  aria-current={!navigateOpen() ? 'page' : undefined}
                  onClick={() => { setQuery(''); if (searchRef) searchRef.value = ''; closeNavigate(); }}
                >
                  <span class="logo-menu-tile-icon">{ICONS.stage()}</span>
                  <span class="logo-menu-tile-body"><span class="logo-menu-tile-title">Canvas</span></span>
                  <span class="logo-menu-badge">{activePage()?.name ?? ''}</span>
                </button>
              </Show>
              {/* OVERVIEW (was Home; renamed 2026-10-16, operator: "the word
                  home is a bit confusing now"): this project's status, quick
                  actions and pages. Where Navigate opens, and the way back from
                  any section. Its pane id stays 'home'. */}
              <button
                type="button"
                class="logo-menu-tile logo-menu-home"
                data-dest="home"
                aria-current={pane() === 'home' && (navigateOpen() || !navigateDocked()) ? 'page' : undefined}
                onClick={() => openPane('home')}
              >
                <span class="logo-menu-tile-icon">{ICONS.overview()}</span>
                <span class="logo-menu-tile-body"><span class="logo-menu-tile-title">Overview</span></span>
              </button>
              <For each={groups()}>
                {(g) => (
                  <div class="logo-menu-group" data-group={g.id}>
                    <h3 class="logo-menu-group-title">{g.title}</h3>
                    <div class="logo-menu-cards">
                      <For each={g.cards}>
                        {(c) => (
                          <button type="button" class={`logo-menu-tile ${c.cls}`} data-dest={c.id} title={c.detail} aria-current={pane() === c.id ? 'page' : undefined} onClick={c.open}>
                            <span class="logo-menu-tile-icon">{ICONS[c.icon]()}</span>
                            <span class="logo-menu-tile-body">
                              <span class="logo-menu-tile-title">{c.title}</span>
                              <span class="logo-menu-tile-meta">{c.detail}</span>
                            </span>
                            <BadgeView b={c.badge} />
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                )}
              </For>
            </nav>

            <div class="sk-mbody logo-menu-body" data-pane={pane()} ref={(el) => (bodyEl = el)}>
              <Show when={pane() === 'home' || searching()} fallback={paneView()}>
              {/* THE PROJECT heads the pane (app-study.html, 2026-10-05): its
                  name, then what it is drawn for, its size and when it was
                  last edited. */}
              <Show when={project()}>
                {(p) => (
                  <div class="logo-menu-pane-head">
                    <h1 class="logo-menu-pane-title">{p().name}</h1>
                    <p class="logo-menu-pane-desc">
                      {frameWords(p().device, p().orientation)}, {plural(p().pages.length, 'page', 'pages')}, {plural(controlCount(), 'control', 'controls')}. Edited {longAge(modifiedAtOf(p()))}.
                    </p>
                  </div>
                )}
              </Show>

              {/* STATUS - Computers, Pre-flight, Now playing (app-study.html):
                  where things stand in words, one button each. Performance
                  mode moved up into the header. */}
              <section class="logo-menu-status" aria-label="Show status">
                <div class="logo-menu-st-card logo-menu-conn" data-tone={conn().tone}>
                  <span class="logo-menu-st-head">{ICONS.computer()}Computers</span>
                  <span class="logo-menu-st-value">{computers().value}</span>
                  <span class="logo-menu-st-meta">{computers().meta}</span>
                  <button type="button" class="logo-menu-st-btn" onClick={() => openPane('connect')}>Manage</button>
                </div>
                <div class="logo-menu-st-card logo-menu-perf-preflight" data-status={preflight().status}>
                  <span class="logo-menu-st-head">{ICONS.preflight()}Pre-flight</span>
                  <span class="logo-menu-st-value">{preflightCard().value}</span>
                  <span class="logo-menu-st-meta">{preflightCard().meta}</span>
                  <button type="button" class="logo-menu-st-btn" onClick={() => openPane('preflight')}>See checks</button>
                </div>
                <div class="logo-menu-st-card logo-menu-now" data-playing={nowPlaying() ? 'true' : 'false'}>
                  <span class="logo-menu-st-head">{ICONS.play()}Now playing</span>
                  <span class="logo-menu-st-value">{nowPlaying()?.value ?? 'Nothing playing'}</span>
                  <span class="logo-menu-st-meta">{nowPlaying()?.meta ?? 'Start a timeline from its launcher or the editor.'}</span>
                  <button type="button" class="logo-menu-st-btn" onClick={() => openPane('timelines')}>Open timelines</button>
                </div>
                {/* APP UPDATE HELD (DEC-229, next-update-plan 1.6). A new version
                    that arrives while the suite is performing - or before this
                    device has heard whether it is - waits (appUpdate.ts); it
                    never reloads the app on stage by itself. A full-width card
                    in the status row, because an action in the build footer is
                    not findable; the consequence (a reload) is stated before
                    the tap. */}
                <Show when={appUpdateReady()}>
                  <button type="button" class="logo-menu-update" onClick={() => applyAppUpdateNow()}>
                    <span class="logo-menu-st-icon">{ICONS.backup()}</span>
                    <span class="logo-menu-st-body">
                      <span class="logo-menu-st-title">Update now</span>
                      <span class="logo-menu-st-meta">
                        A new version of OPButtons is ready.
                        {performanceMode()
                          ? ' It is waiting because the suite is in performance mode.'
                          : ' It is waiting until OPButtons hears from OPBridge whether the suite is performing.'}
                        {' '}Tap to reload into it - timelines and engines keep running on OPBridge.
                      </span>
                    </span>
                  </button>
                </Show>
              </section>

              {/* No "Fit to this screen" banner any more (operator, 2026-09-30,
                  asked once this sheet worked on a phone): it offered to redraw
                  the project for WHATEVER screen opened Navigate - an iPad gig
                  project at 390x844 on an iPhone, at the window size on a desktop
                  - and projects sync. Re-framing stays with the installed app's
                  automatic fit (DEC-210) and the project browser's device chip. */}

              <Show when={showSetup()}>
                <section class="logo-menu-setup" aria-label="Get set up">
                  <div class="logo-menu-setup-head">
                    <span class="logo-menu-setup-title">Get set up</span>
                    <span class="logo-menu-setup-count">
                      {setupSteps().filter((s) => s.done).length} of {setupSteps().length} done
                    </span>
                  </div>
                  <div class="logo-menu-setup-steps">
                    <For each={setupSteps()}>
                      {(s, i) => (
                        <button type="button" class="logo-menu-setup-step" data-done={s.done ? 'true' : 'false'} onClick={s.run}>
                          <span class="logo-menu-setup-n" aria-hidden="true">{s.done ? '✓' : i() + 1}</span>
                          {s.title}
                        </button>
                      )}
                    </For>
                    <button type="button" class="logo-menu-setup-step logo-menu-setup-tour" onClick={() => go(props.onGettingStarted)}>
                      <span class="logo-menu-setup-n" aria-hidden="true">?</span>
                      What is OPButtons?
                    </button>
                  </div>
                  <button
                    type="button"
                    class="logo-menu-setup-hide"
                    onClick={() => { setSetupHidden(true); writeFlag(SETUP_HIDDEN_KEY, true); }}
                  >
                    Hide
                  </button>
                </section>
              </Show>

              <Show
                when={!searching()}
                fallback={
                  <section class="logo-menu-results" id="logo-menu-results" role="listbox" aria-label="Search results">
                    <Show
                      when={results().length > 0}
                      fallback={
                        <p class="logo-menu-noresult">
                          Nothing called <b>"{query().trim()}"</b> yet. Try <b>lights</b>, <b>pedal</b>, <b>backup</b> or <b>new project</b>.
                        </p>
                      }
                    >
                      <For each={results()}>
                        {(r, i) => (
                          <button
                            type="button"
                            class="logo-menu-result"
                            role="option"
                            data-kind={r.kind}
                            data-active={i() === active() ? 'true' : 'false'}
                            aria-selected={i() === active()}
                            onClick={() => r.run()}
                            onPointerEnter={() => setActive(i())}
                          >
                            <span class="logo-menu-tile-icon">{ICONS[r.icon]()}</span>
                            <span class="logo-menu-result-body">
                              <span class="logo-menu-result-title"><Title text={r.title} /></span>
                              <span class="logo-menu-result-detail">{r.detail}</span>
                            </span>
                            <span class="logo-menu-result-where">{r.where}</span>
                            <kbd class="logo-menu-result-enter" aria-hidden="true">↵</kbd>
                          </button>
                        )}
                      </For>
                    </Show>
                  </section>
                }
              >
                <section class="logo-menu-sec logo-menu-actions" aria-label="Do something">
                  <h2 class="logo-menu-sec-title">Do something</h2>
                  <div class="logo-menu-action-grid">
                    <For each={actions()}>
                      {(a) => (
                        <button type="button" class="logo-menu-action" data-action={a.id} onClick={a.run}>
                          <span class="logo-menu-action-icon">{ICONS[a.icon]()}</span>
                          <span class="logo-menu-action-body">
                            <span class="logo-menu-action-title">{a.title}</span>
                            <span class="logo-menu-action-detail">{a.detail}</span>
                          </span>
                        </button>
                      )}
                    </For>
                  </div>
                </section>

                <Show when={thumbs().length > 0}>
                  <section class="logo-menu-sec logo-menu-pagethumbs" aria-label="Your pages">
                    <div class="logo-menu-sec-head">
                      <h2 class="logo-menu-sec-title">Your pages</h2>
                      <span class="logo-menu-sec-note">Tap one to play it</span>
                    </div>
                    <div class="logo-menu-thumb-grid">
                      <For each={thumbs()}>
                        {(t) => (
                          <button
                            type="button"
                            class="logo-menu-thumb"
                            aria-current={t.current ? 'page' : undefined}
                            aria-label={`Page ${t.n}, ${t.name}, ${plural(t.count, 'control', 'controls')}`}
                            onClick={() => go(() => switchPage(t.id))}
                          >
                            <span class="logo-menu-thumb-mini" style={{ 'aspect-ratio': t.aspect }}>
                              <For each={t.boxes}>
                                {(b) => <i style={{ left: b.left, top: b.top, width: b.width, height: b.height, '--c': b.color }} />}
                              </For>
                              <Show when={t.count === 0}>
                                <span class="logo-menu-thumb-empty">Empty</span>
                              </Show>
                            </span>
                            <span class="logo-menu-thumb-label">
                              <span class="logo-menu-thumb-dot" style={{ background: `var(--page-hue-${t.hue})` }} />
                              <span class="logo-menu-thumb-name">{t.n}. {t.name}</span>
                            </span>
                            <span class="logo-menu-thumb-meta">
                              {plural(t.count, 'control', 'controls')}{t.current ? ', on screen now' : ''}
                            </span>
                          </button>
                        )}
                      </For>
                    </div>
                  </section>
                </Show>

                {/* Jump back in (app-study.html): the last things touched. Projects
                    live in the Project section now (the card at the top of the list). */}
                <section class="logo-menu-sec logo-menu-jump" aria-label="Jump back in">
                  <div class="logo-menu-sec-head">
                    <h2 class="logo-menu-sec-title">Jump back in</h2>
                  </div>
                  <JumpBackIn onPick={jumpBack} />
                </section>
              </Show>
              </Show>
            </div>
          </div>

          {/* The build footer moved to Settings > About (app-study.html,
              2026-10-05), with Details and the device's frame report. */}
        </div>
        {/* Popups a section opens mount here while Navigate is open, so they
            sit above it (layout/shellPortal.ts). */}
        <div class="navigate-portal" ref={(el) => { portalEl = el; setNavigatePortal(el); }} />
        {/* Docked with the chrome hidden and a section open, the section has
            the window; this is the way back to Navigate (DEC-268). */}
        <Show when={navigateDocked() && dockHidden() && navigateOpen()}>
          <button type="button" class="nav-dock-corner" onClick={() => setDockHidden(false)} title="Show Navigate (\)" aria-label="Show Navigate">
            <img src="/icons/icon-192.png" alt="" />
          </button>
        </Show>
      </div>
    </Portal>
  );
}
